import { FC, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useCompanyTagsContext } from 'application/components/common';
import { useAppDispatch, useAppSelector } from 'application/providers/StoreProvider';

import { AdCampaignMediaTypeEnum } from 'domain/api/graphql/generated';
import { AdCampaignTypeEnum } from 'domain/media/types';

import DateUtils from '@zvuk-b2b/react-uikit/toolkit/DateUtils';
import { WeekDaysType } from '@zvuk-b2b/react-uikit/toolkit/DateUtils/types';
import Autocomplete from '@zvuk-b2b/react-uikit/ui/Autocomplete';
import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import { DropdownPlacement } from '@zvuk-b2b/react-uikit/ui/Dropdown';
import Input, { InputType } from '@zvuk-b2b/react-uikit/ui/Input';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import IconChevronDown from '@zvuk-b2b/react-uikit/ui/icons/ChevronDown';

import AdTypeSelect from '../../AdsDrawer/AdCampaignForm/AdTypeSelect';
import BroadcastDates from '../../AdsDrawer/AdCampaignForm/BroadcastDates';
import BroadcastTimes from '../../AdsDrawer/AdCampaignForm/BroadcastTimes';
import { MAX_VALUE_BY_MODE, MIN_VALUE_BY_MODE } from '../../AdsDrawer/AdCampaignForm/consts';
import WeekdaysSettings from '../../AdsDrawer/AdCampaignForm/WeekdaysSettings';
import {
  selectCampaignType,
  selectCampaignName,
  selectValueByMode,
  selectStartDate,
  selectEndDate,
  selectIsInfinitely,
  selectWeekdays,
  selectDayStartTime,
  selectDayEndTime,
  selectIsInstantAdCampaignDisabled,
} from '../../../slices/createAdCampaign/selectors';
import { CREATE_AD_CAMPAIGN_STEPS } from '../../../slices/createAdCampaign/consts';
import {
  setCampaignType,
  setCampaignName,
  setStep,
  setValueByMode,
  setStartDate,
  setEndDate,
  setIsInfinitely,
  setWeekday,
  setDayStartTime,
  setDayEndTime,
} from '../../../slices/createAdCampaign/slice';
import AdCampaignTypeHint from './AdCampaignTypeHint';
import { AD_TYPE_HINT_TITLES } from './consts';
import styles from './index.module.scss';
import { TEXTS } from './texts';

const CAMPAIGN_NAME_MAX_LENGTH = 128;

export type AdCampaignStepperSettingsProps = {
  mediaType?: AdCampaignMediaTypeEnum;
  onBack?: () => void;
  onNext?: () => void;
  close?: () => void;
};

const AdCampaignStepperSettings: FC<AdCampaignStepperSettingsProps> = ({
  mediaType = AdCampaignMediaTypeEnum.Audio,
  onBack,
  onNext,
  close,
}) => {
  const dispatch = useAppDispatch();

  const campaignType = useAppSelector(selectCampaignType);
  const campaignName = useAppSelector(selectCampaignName);
  const valueByMode = useAppSelector(selectValueByMode);
  const startDateStr = useAppSelector(selectStartDate);
  const endDateStr = useAppSelector(selectEndDate);
  const isInfinitely = useAppSelector(selectIsInfinitely);
  const weekdays = useAppSelector(selectWeekdays);
  const dayStartTime = useAppSelector(selectDayStartTime);
  const dayEndTime = useAppSelector(selectDayEndTime);
  const isInstantAdCampaignDisabled = useAppSelector(selectIsInstantAdCampaignDisabled);

  const [isHintExpanded, setIsHintExpanded] = useState(false);
  const [isAutocompleteFullMode, setIsAutocompleteFullMode] = useState(false);

  const startDate = startDateStr ? new Date(startDateStr) : DateUtils.getStartOfDay(DateUtils.NOW);
  const endDate = endDateStr ? new Date(endDateStr) : DateUtils.getEndOfDay(DateUtils.NOW);

  const {
    value: tagsValue,
    options: tagsOptions,
    maxVisibleOptions: tagsMaxVisibleOptions,
    extensions: tagsExtensions,
    inputRef: tagsInputRef,
    isLoading: isTagsLoading,
    isAddTagLoading,
    onChange: handleTagsChange,
    onBackspacePressed: handleTagsBackspacePressed,
    onEnterPressed: handleTagsEnterPressed,
    tagsRequest,
  } = useCompanyTagsContext();

  const isInstant = campaignType === AdCampaignTypeEnum.Instant;
  const isInterval = campaignType === AdCampaignTypeEnum.Interval;
  const isPeriod = campaignType === AdCampaignTypeEnum.Period;
  const isCampaignNameInvalid = campaignName.trim().length === 0 || campaignName.length > CAMPAIGN_NAME_MAX_LENGTH;

  const handleInstantDisabledHintClick = useCallback(() => {
    if (!isCampaignNameInvalid) {
      dispatch(setStep(CREATE_AD_CAMPAIGN_STEPS.MEDIA));
    }
  }, [dispatch, isCampaignNameInvalid]);

  useEffect(() => {
    if (campaignType === AdCampaignTypeEnum.ExactTime) {
      dispatch(setIsInfinitely(true));
    } else if (campaignType === AdCampaignTypeEnum.Interval || campaignType === AdCampaignTypeEnum.Period) {
      dispatch(setIsInfinitely(false));
    }
  }, [campaignType]);

  useEffect(() => {
    tagsRequest('');
  }, [tagsRequest]);

  const handleCampaignTypeChange = useCallback(
    (type: AdCampaignTypeEnum) => {
      dispatch(setCampaignType(type));
      setIsHintExpanded(false);
    },
    [dispatch]
  );

  const handleWeekdayChange = (key: string, value: boolean) => {
    dispatch(setWeekday({ key: key as keyof WeekDaysType, value }));
  };

  const normalizeValueByMode = () => {
    dispatch(
      setValueByMode(`${Math.min(Math.max(+valueByMode || MIN_VALUE_BY_MODE, MIN_VALUE_BY_MODE), MAX_VALUE_BY_MODE)}`)
    );
  };

  const handleStartDateChange = (date: Date | null) => {
    dispatch(setStartDate(date ? date.toISOString() : null));
  };

  const handleEndDateChange = (date: Date | null) => {
    dispatch(setEndDate(date ? date.toISOString() : null));
  };

  const actionButtons = [
    <Button
      key="button_ad_campaign_stepper_settings_next"
      elementId="button_ad_campaign_stepper_settings_next"
      type={ButtonType.PRIMARY}
      isDisabled={isCampaignNameInvalid}
      onClick={onNext}>
      {TEXTS.NEXT}
    </Button>,
  ];

  if (onBack) {
    actionButtons.push(
      <Button
        key="button_ad_campaign_stepper_settings_back"
        elementId="button_ad_campaign_stepper_settings_back"
        type={ButtonType.SECONDARY}
        onClick={onBack}>
        {TEXTS.BACK}
      </Button>
    );
  }

  return (
    <ModalScreen
      title={mediaType === AdCampaignMediaTypeEnum.Audio ? TEXTS.AUDIO_TITLE : TEXTS.VIDEO_TITLE}
      titlePlacement="left"
      onClose={close}
      containerClassName={styles.container}
      titleClassName={styles.title}
      contentClassName={styles.content}
      hideCloseButton={isAutocompleteFullMode}
      actionButtons={actionButtons}>
      <div className={styles.root}>
        <section className={styles.section}>
          <h4 className={styles.subtitle}>{TEXTS.CAMPAIGN_NAME_SECTION_TITLE}</h4>
          <Input
            elementId="input_ad_campaign_stepper_name"
            label={TEXTS.CAMPAIGN_NAME_LABEL}
            placeholder={TEXTS.CAMPAIGN_NAME_PLACEHOLDER}
            type={InputType.TEXT}
            value={campaignName}
            forceError={campaignName.length > CAMPAIGN_NAME_MAX_LENGTH}
            onChange={(v) => dispatch(setCampaignName(v))}
          />
          <span
            data-testid="hint_ad_campaign_stepper_name_limit"
            className={classNames(styles.hint, campaignName.length > CAMPAIGN_NAME_MAX_LENGTH && styles.hintError)}>
            {TEXTS.CAMPAIGN_NAME_MAX_LENGTH_HINT}
          </span>
        </section>

        <section className={styles.section}>
          <h4 className={styles.subtitle}>{TEXTS.FORM_BLOCK_MODE_TITLE}</h4>
          <AdTypeSelect
            containerClassName={styles.typeSelect}
            value={campaignType}
            isInstantAdCampaignDisabled={isInstantAdCampaignDisabled}
            isInstantDisabledHintLinkDisabled={isCampaignNameInvalid}
            isLoading={false}
            mediaType={mediaType}
            setPromoTab={handleInstantDisabledHintClick}
            onChange={handleCampaignTypeChange}
          />

          <div className={styles.info}>
            <button
              id="button_ad_campaign_stepper_type_info"
              data-testid="button_ad_campaign_stepper_type_info"
              type="button"
              className={styles.infoHeader}
              data-expanded={isHintExpanded}
              onClick={() => setIsHintExpanded((v) => !v)}>
              <span className={styles.infoTitle}>
                {AD_TYPE_HINT_TITLES[campaignType as keyof typeof AD_TYPE_HINT_TITLES]}
              </span>
              <IconChevronDown
                className={styles.infoIcon}
                aria-hidden
              />
            </button>

            {isHintExpanded && (
              <div className={styles.infoContent}>
                <AdCampaignTypeHint campaignType={campaignType} />
              </div>
            )}
          </div>

          {isInterval && (
            <Input
              elementId="input_ad_campaign_stepper_interval"
              label={TEXTS.INPUT_MODE_INTERVAL_TITLE}
              type={InputType.TEXT}
              value={valueByMode}
              onChange={(v) => dispatch(setValueByMode(v))}
              onBlur={normalizeValueByMode}
            />
          )}

          {isPeriod && (
            <Input
              elementId="input_ad_campaign_stepper_period"
              label={TEXTS.INPUT_MODE_PERIOD_TITLE}
              type={InputType.TEXT}
              value={valueByMode}
              onChange={(v) => dispatch(setValueByMode(v))}
              onBlur={normalizeValueByMode}
            />
          )}
        </section>

        {!isInstant && (
          <>
            <section className={styles.section}>
              <BroadcastDates
                isInline
                isInfinitely={isInfinitely}
                startDate={startDate}
                endDate={endDate}
                setStartDate={handleStartDateChange}
                setEndDate={handleEndDateChange}
                handleChangeIsInfinitely={(v) => dispatch(setIsInfinitely(v))}
                startElementId="input_ad_campaign_stepper_start_date"
                endElementId="input_ad_campaign_stepper_end_date"
              />
            </section>

            <section className={styles.section}>
              <WeekdaysSettings
                days={weekdays}
                isLoading={false}
                handleDay={handleWeekdayChange}
              />
            </section>

            <section className={styles.section}>
              <BroadcastTimes
                dayStartTime={dayStartTime}
                dayEndTime={dayEndTime}
                setDayStartTime={(v) => dispatch(setDayStartTime(v))}
                setDayEndTime={(v) => dispatch(setDayEndTime(v))}
                startElementId="input_ad_campaign_stepper_start_time"
                endElementId="input_ad_campaign_stepper_end_time"
                endPlaceholder="23:59"
              />
            </section>
          </>
        )}

        <section className={styles.section}>
          <Autocomplete
            elementId="autocomplete_ad_campaign_stepper_tags"
            autoComplete="off"
            label={TEXTS.TAGS_LABEL}
            value={tagsValue}
            options={tagsOptions}
            inputRef={tagsInputRef}
            placeholder={tagsExtensions.length === 0 ? TEXTS.TAGS_PLACEHOLDER : ''}
            containerClassName={styles.tagsAutocomplete}
            inputClassName={styles.tagsAutocompleteInput}
            clearIconClassName={styles.tagsAutocompleteClearIcon}
            dropdownClassName={styles.tagsAutocompleteDropdown}
            adornmentRight={
              <button
                type="button"
                className={styles.tagsAutocompleteIconButton}
                aria-label={TEXTS.TAGS_OPEN_LABEL}
                onMouseDown={(event) => {
                  event.preventDefault();
                  tagsInputRef.current?.focus();
                }}>
                <IconChevronDown className={styles.tagsAutocompleteIcon} />
              </button>
            }
            isMulti
            useClearButton
            isLoading={isTagsLoading}
            dropdownPlacement={DropdownPlacement.BOTTOM}
            isNeedCloseWhileParentScrolling={false}
            isDisabled={isAddTagLoading}
            extensions={tagsExtensions}
            maxVisibleOptions={tagsMaxVisibleOptions}
            maxOptions={tagsOptions.length}
            menuMargin={4}
            onChange={handleTagsChange}
            onBackspacePressed={handleTagsBackspacePressed}
            onEnterPressed={handleTagsEnterPressed}
            onFullModeChange={setIsAutocompleteFullMode}
          />
        </section>
      </div>
    </ModalScreen>
  );
};

export default AdCampaignStepperSettings;
